import React, { useState, useEffect } from "react";
import {
  Phone,
  Mail,
  Users,
  CheckCircle,
  AlertCircle,
  Bell,
  UserPlus,
  Search,
  Filter,
  TrendingUp,
} from "lucide-react";
import { useHistory } from "react-router-dom";
import { useAppContext } from "../../AppContext";
import { MyDomain } from "../../App";
import axios from "axios";
import { useTranslation } from "react-i18next";
import AccountantInviteModal from "./AccountantContractorComps/AccountantInviteModal";
import "./AccountantHome.css";

export default function AccountantHome() {
  const { t } = useTranslation();
  const history = useHistory();
  const { User, Lang } = useAppContext();
  const isRTL = Lang === "ar" || Lang === "he";

  const [contractors, setContractors] = useState([]);
  const [filteredContractors, setFilteredContractors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [inviteModalVisible, setInviteModalVisible] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");

  useEffect(() => {
    fetchContractors();
  }, []);

  useEffect(() => {
    if (searchQuery.trim() === "") {
      setFilteredContractors(contractors);
    } else {
      const filtered = contractors.filter(
        (contractor) =>
          contractor.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
          contractor.email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
          contractor.phoneNumber?.includes(searchQuery),
      );
      setFilteredContractors(filtered);
    }
  }, [searchQuery, contractors]);

  const fetchContractors = async () => {
    try {
      setLoading(true);
      const response = await axios.post(
        `${MyDomain}Accountant/AccountantGetAllContractors`,
        {
          Id: User.id,
        },
      );

      if (response.data && response.data.myContractors) {
        setContractors(response.data.myContractors);
        setFilteredContractors(response.data.myContractors);
        console.log("✅ Contractors loaded:", response.data.myContractors);
      }
    } catch (error) {
      console.error("❌ Error fetching contractors:", error);
      alert(t("failedToLoadContractors"));
    } finally {
      setLoading(false);
    }
  };

  const handleContractorPress = (contractor) => {
    history.push({
      pathname: "/accountant/contractor-details",
      state: {
        contractorId: contractor.id,
        contractorName: contractor.name,
        contractorData: contractor,
      },
    });
  };

  const handleCall = (phoneNumber, event) => {
    if (event) {
      event.stopPropagation();
    }
    if (phoneNumber) {
      window.location.href = `tel:${phoneNumber}`;
    } else {
      alert(t("phoneNumberNotAvailable"));
    }
  };

  const handleEmail = (email, event) => {
    if (event) {
      event.stopPropagation();
    }
    if (email) {
      window.location.href = `mailto:${email}`;
    } else {
      alert(t("emailNotAvailable"));
    }
  };

  const getInitials = (name) => {
    if (!name) return "?";
    const words = name.trim().split(" ");
    if (words.length === 1) return name.substring(0, 2).toUpperCase();
    return (words[0][0] + words[words.length - 1][0]).toUpperCase();
  };

  const getStatusColor = () => {
    return "#34C759";
  };

  const getStatusText = () => {
    return t("active");
  };

  const renderContractorCard = (item, index) => (
    <div
      key={item.id}
      className={`contractor-card ${isRTL ? "rtl" : ""}`}
      onClick={() => handleContractorPress(item)}
      style={{ animationDelay: `${index * 50}ms` }}
    >
      {/* Header with Avatar and Info */}
      <div className={`card-header ${isRTL ? "rtl" : ""}`}>
        <div className="avatar-container">
          <div
            className="avatar"
            style={{ backgroundColor: getStatusColor() + "20" }}
          >
            <span className="avatar-text" style={{ color: getStatusColor() }}>
              {getInitials(item.name)}
            </span>
          </div>
          <div
            className="status-badge"
            style={{ backgroundColor: getStatusColor() }}
          />
        </div>

        <div className={`contractor-info ${isRTL ? "rtl" : ""}`}>
          <h3 className={`contractor-name ${isRTL ? "rtl" : ""}`}>
            {item.name}
          </h3>
          <div className={`status-container ${isRTL ? "rtl" : ""}`}>
            <div
              className="status-pill"
              style={{ backgroundColor: getStatusColor() + "20" }}
            >
              <CheckCircle size={12} color={getStatusColor()} />
              <span className="status-text" style={{ color: getStatusColor() }}>
                {getStatusText()}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Contact Info */}
      <div className="contact-section">
        {item.phoneNumber && (
          <div
            className={`contact-item ${isRTL ? "rtl" : ""}`}
            onClick={(e) => {
              e.stopPropagation();
              handleCall(item.phoneNumber);
            }}
          >
            <div className="icon-wrapper phone-icon">
              <Phone size={16} />
            </div>
            <span className={`contact-text ${isRTL ? "rtl" : ""}`}>
              {item.phoneNumber}
            </span>
          </div>
        )}

        {item.email && (
          <div
            className={`contact-item ${isRTL ? "rtl" : ""}`}
            onClick={(e) => {
              e.stopPropagation();
              handleEmail(item.email);
            }}
          >
            <div className="icon-wrapper email-icon">
              <Mail size={16} />
            </div>
            <span className={`contact-text ${isRTL ? "rtl" : ""}`}>
              {item.email}
            </span>
          </div>
        )}

        {!item.phoneNumber && !item.email && (
          <div className="no-contact-container">
            <AlertCircle size={16} color="#999" />
            <span className={`no-contact-text ${isRTL ? "rtl" : ""}`}>
              {t("noContactInfo")}
            </span>
          </div>
        )}
      </div>

      {/* Footer with ID */}
      <div className={`card-footer ${isRTL ? "rtl" : ""}`}>
        <span className={`id-label ${isRTL ? "rtl" : ""}`}>
          {t("id")}: {item.id}
        </span>
        {item.expoToken && (
          <div className="notification-badge">
            <Bell size={14} color="#007AFF" />
          </div>
        )}
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="loading-container">
        <div className="loader"></div>
        <p className={`loading-text ${isRTL ? "rtl" : ""}`}>
          {t("loadingContractors")}
        </p>
      </div>
    );
  }

  return (
    <div className={`accountant-home-container ${isRTL ? "rtl" : ""}`}>
      {/* Header */}
      <div className="page-header">
        <div className={`header-top ${isRTL ? "rtl" : ""}`}>
          <div className="header-title-section">
            <h1 className={`page-title ${isRTL ? "rtl" : ""}`}>
              {t("myContractors")}
            </h1>
            <p className={`page-subtitle ${isRTL ? "rtl" : ""}`}>
              {t("manageAndTrackYourContractors")}
            </p>
          </div>

          <button
            className={`invite-button-primary ${isRTL ? "rtl" : ""}`}
            onClick={() => setInviteModalVisible(true)}
          >
            <UserPlus size={20} />
            <span>{t("inviteContractor")}</span>
          </button>
        </div>

        {/* Stats Cards */}
        <div className={`stats-grid ${isRTL ? "rtl" : ""}`}>
          <div className="stat-card primary">
            <div className="stat-icon-wrapper">
              <Users size={24} />
            </div>
            <div className={`stat-details ${isRTL ? "rtl" : ""}`}>
              <p className={`stat-label ${isRTL ? "rtl" : ""}`}>
                {t("totalContractors")}
              </p>
              <h2 className={`stat-value ${isRTL ? "rtl" : ""}`}>
                {contractors.length}
              </h2>
            </div>
          </div>

          <div className="stat-card success">
            <div className="stat-icon-wrapper">
              <CheckCircle size={24} />
            </div>
            <div className={`stat-details ${isRTL ? "rtl" : ""}`}>
              <p className={`stat-label ${isRTL ? "rtl" : ""}`}>
                {t("activeContractors")}
              </p>
              <h2 className={`stat-value ${isRTL ? "rtl" : ""}`}>
                {contractors.length}
              </h2>
            </div>
          </div>

          <div className="stat-card info">
            <div className="stat-icon-wrapper">
              <TrendingUp size={24} />
            </div>
            <div className={`stat-details ${isRTL ? "rtl" : ""}`}>
              <p className={`stat-label ${isRTL ? "rtl" : ""}`}>
                {t("thisMonth")}
              </p>
              <h2 className={`stat-value ${isRTL ? "rtl" : ""}`}>
                {contractors.length}
              </h2>
            </div>
          </div>
        </div>
      </div>

      {/* Search and Filter Section */}
      <div className={`search-filter-section ${isRTL ? "rtl" : ""}`}>
        <div className={`search-box ${isRTL ? "rtl" : ""}`}>
          <Search size={20} className="search-icon" />
          <input
            type="text"
            placeholder={t("searchContractors")}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className={`search-input ${isRTL ? "rtl" : ""}`}
            dir={isRTL ? "rtl" : "ltr"}
          />
        </div>
      </div>

      {/* Contractors List */}
      {filteredContractors.length === 0 ? (
        <div className="empty-container">
          {searchQuery ? (
            <>
              <Search size={80} color="#CCC" />
              <h2 className={`empty-text ${isRTL ? "rtl" : ""}`}>
                {t("noResultsFound")}
              </h2>
              <p className={`empty-subtext ${isRTL ? "rtl" : ""}`}>
                {t("tryDifferentSearchTerm")}
              </p>
            </>
          ) : (
            <>
              <Users size={80} color="#CCC" />
              <h2 className={`empty-text ${isRTL ? "rtl" : ""}`}>
                {t("noContractorsFound")}
              </h2>
              <p className={`empty-subtext ${isRTL ? "rtl" : ""}`}>
                {t("contractorsWillAppearHere")}
              </p>
              <button
                className="empty-action-button"
                onClick={() => setInviteModalVisible(true)}
              >
                <UserPlus size={20} />
                <span>{t("inviteYourFirstContractor")}</span>
              </button>
            </>
          )}
        </div>
      ) : (
        <div className="contractors-grid">
          {filteredContractors.map((item, index) =>
            renderContractorCard(item, index),
          )}
        </div>
      )}

      {/* Invite Modal */}
      {inviteModalVisible && (
        <AccountantInviteModal
          visible={inviteModalVisible}
          onClose={() => setInviteModalVisible(false)}
          accountantId={User.id}
          onInviteSent={() => {
            fetchContractors();
          }}
        />
      )}
    </div>
  );
}
